import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { Button, Typography } from "@mui/material";
import { useViewport } from "../../helpers";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ImageList from "../../components/imageList/ImageList";
import { useSnackbar } from "notistack";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation, useParams } from "react-router-dom";
import { openBackdrop } from "../../reducers/expariences/expariencesSlice";
import {
  fetchMyExpariences,
  fetchSingleExparience,
} from "../../reducers/expariences/expariencesThunks";

const PREFIX = "MyExpariences";

const classes = {
  updateButtonContainer: `${PREFIX}-updateButtonContainer`,
  pullToRefresh: `${PREFIX}-pullToRefresh`,
  containerMarginTop: `${PREFIX}-containerMarginTop`,
  backdrop: `${PREFIX}-backdrop`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.updateButtonContainer}`]: {
    marginBottom: "0.5rem",
    height: "40px",
    display: "flex",
    justifyContent: "flex-end",
  },

  [`& .${classes.pullToRefresh}`]: {
    textAlign: "center",
  },

  [`& .${classes.containerMarginTop}`]: {
    marginTop: "1.5rem",
  },

  [`& .${classes.backdrop}`]: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const FETCH_LIMIT = 20;

const MyExpariences = () => {
  const { width } = useViewport();
  const { t } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const loading = useSelector((state) => state.myExpariences.loading);
  const expariences = useSelector((state) => state.myExpariences.myExpariences);
  const role = useSelector((state) => state.user.currentUser.account.role);
  const fetchingLoader = useSelector(
    (state) => state.myExpariences.fetchingLoader
  );
  const offset = useSelector((state) => state.myExpariences?.offset);
  const hasMore = useSelector((state) => state.myExpariences?.hasMore);

  const getMyExpariences = async (
    resetMode = false,
    offsetParam = 0,
    fetchLimit = FETCH_LIMIT,
    isUserApi = false
  ) => {
    try {
      const thunk = await dispatch(
        fetchMyExpariences({
          offset: offsetParam,
          limit: fetchLimit,
          reset: resetMode,
          isUserApi,
        })
      );
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const getSingleExparience = async (id, isAdmin, redirect = false) => {
    try {
      const thunk = await dispatch(fetchSingleExparience({ id, isAdmin }));
      if (thunk.meta.requestStatus === "rejected") throw new Error("offline");
      if (redirect) dispatch(openBackdrop({ from: "modify" }));
    } catch {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  useEffect(() => {
    if (role === "ADMIN") {
      getMyExpariences(true);
    } else {
      getMyExpariences(true, 0, FETCH_LIMIT, true);
    }
    if (location.state && location.state.id) {
      getSingleExparience(location.state.id, role === "ADMIN");
      dispatch(openBackdrop({ from: "modify", infos: {} }));
    } else if (id) {
      getSingleExparience(id, role === "ADMIN", true);
    }
  }, []);

  useEffect(() => {
    if (location.state && location.state.type === "open") {
      delete location.state.type;
      getSingleExparience(id, role === "ADMIN", true);
    }
  }, [location.state]);

  useEffect(() => {
    if (id) {
      getSingleExparience(id, role === "ADMIN", true);
    }
  }, [id]);

  function handleRefreshDesktop() {
    if (role === "ADMIN") {
      getMyExpariences(true);
    } else {
      getMyExpariences(true, 0, FETCH_LIMIT, true);
    }
  }

  const handleRefreshMobile = async () => {
    if (role === "ADMIN") {
      getMyExpariences(true);
    } else {
      getMyExpariences(true, 0, FETCH_LIMIT, true);
    }
  };

  const handleFetchByRole = () => {
    if (role === "ADMIN" && !loading)
      getMyExpariences(false, parseInt(offset) + FETCH_LIMIT, FETCH_LIMIT);
    else
      getMyExpariences(
        false,
        parseInt(offset) + FETCH_LIMIT,
        FETCH_LIMIT,
        true
      );
  };

  if (!expariences) {
    return (
      <Container>
        <div className="loading-container">
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Container>
    );
  } else {
    if (expariences.length >= 0) {
      return (
        <StyledDiv>
          <Container className={classes.containerMarginTop}>
            <Backdrop className={classes.backdrop} open={fetchingLoader}>
              <CircularProgress color="inherit" />
            </Backdrop>
            {width > 768 && hasMore ? (
              <React.Fragment>
                <div className={classes.updateButtonContainer}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => !loading && handleRefreshDesktop()}
                  >
                    {loading ? (
                      <CircularProgress size={28} />
                    ) : (
                      t("myExpariences.refresh")
                    )}
                  </Button>
                </div>
                <InfiniteScroll
                  dataLength={expariences.length} //This is important field to render the next data
                  next={() => (!loading ? handleFetchByRole() : undefined)}
                  hasMore={hasMore}
                  loader={
                    <div style={{ height: "100px" }}>
                      <CircularProgress size={50} />
                    </div>
                  }
                >
                  <ImageList myExpariences={expariences} />
                </InfiniteScroll>
              </React.Fragment>
            ) : (
              <div>
                <InfiniteScroll
                  dataLength={expariences.length} //This is important field to render the next data
                  next={() => (!loading ? handleFetchByRole() : undefined)}
                  hasMore={hasMore}
                  loader={
                    <div style={{ height: "100px" }}>
                      <CircularProgress size={50} />
                    </div>
                  }
                  refreshFunction={handleRefreshMobile}
                  pullDownToRefresh
                  pullDownToRefreshThreshold={150}
                  pullDownToRefreshContent={
                    <h3 style={{ textAlign: "center" }}>
                      &#8595; {t("infiniteScroll.pullDownToRefresh")}
                    </h3>
                  }
                  releaseToRefreshContent={
                    <h3 style={{ textAlign: "center" }}>
                      &#8593; {t("infiniteScroll.releaseToRefresh")}
                    </h3>
                  }
                >
                  <ImageList myExpariences={expariences} />
                </InfiniteScroll>
              </div>
            )}
          </Container>
        </StyledDiv>
      );
    } else {
      return (
        <Container>
          <div className="loading-container">
            <Typography>{t("myExpariences.noResults")}</Typography>
          </div>
        </Container>
      );
    }
  }
};

export default MyExpariences;
